import { cva } from 'class-variance-authority';

import { Card } from '@/components/Card/card';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { cn } from '@/lib/cn';

import Icon from '../Icon/Icon';

import {
  resolveContainerProps,
  resolveStepItemProps,
} from './ProgressStepBar.utils';

type Props = {
  /** default: full */
  prefixVariant?: 'full' | 'icon' | 'none';
  stepLabels: string[];
  /** default: 0 */
  currentStepIndex?: number;
  onStepClick?: (index: number) => void;
  /** prefixVariant="full" 時に表示されるラベル。 default: "Steps" */
  stepPrefixLabel?: string;
};

/**
 * 複数のステップからなる進捗状況を示すバー型のコンポーネント
 * @see https://www.figma.com/design/xOnr21NAsSbNuEglbEeZog/901_Components?node-id=3505-3831&p=f&t=3Cv8MPRLvwBWzcWx-0
 */
export const ProgressStepBar = (props: Props) => {
  const prefixVariant: Props['prefixVariant'] = props.prefixVariant ?? 'full';
  const currentStepIndex = props.currentStepIndex ?? 0;
  const stepPrefixLabel = props.stepPrefixLabel ?? 'Steps';
  const containerProps = resolveContainerProps({
    hasStepClick: !!props.onStepClick,
  });

  return (
    <Card className="shadow-inset-small rounded-sm bg-neutral-bottom-fill w-fit">
      <HStack className={'p-2 gap-2 items-center'} role={containerProps.role}>
        {prefixVariant !== 'none' && (
          <HStack className="gap-1 items-center">
            {prefixVariant === 'full' && (
              <Text variant="pSmall" className="text-neutral-muted-on-fill">
                {stepPrefixLabel}
              </Text>
            )}
            <Icon iconName="CircleArrowRight" colorVariant="muted" />
          </HStack>
        )}

        {props.stepLabels.map((label, index) => {
          const variant =
            index < currentStepIndex
              ? 'completed'
              : index === currentStepIndex
                ? 'current'
                : 'upcoming';

          return (
            <StepItem
              key={index}
              first={index === 0}
              last={index === props.stepLabels.length - 1}
              variant={variant}
              label={label}
              onClick={
                props.onStepClick ? () => props.onStepClick?.(index) : undefined
              }
              isSelected={index === currentStepIndex}
            />
          );
        })}
      </HStack>
    </Card>
  );
};

const StepItem = (props: {
  first?: boolean;
  last?: boolean;
  label: string;
  variant: 'completed' | 'current' | 'upcoming';
  onClick?: () => void;
  isSelected: boolean;
}) => {
  const {
    element: Root,
    type,
    role,
    ariaSelected,
  } = resolveStepItemProps({
    onClick: props.onClick,
    isSelected: props.isSelected,
  });
  const isInteractive = !!props.onClick;

  const styleVariants = cva('', {
    variants: {
      color: {
        completed:
          'bg-neutral-50 text-neutral-muted-on-fill border-neutral-muted-border',
        current:
          'bg-decorative-slate-black-muted-fill text-primary border-primary-muted-border',
        upcoming: 'bg-neutral-fill text-primary border-neutral-fill',
      },
      shapeLeft: {
        first: 'w-1',
        other: 'w-[10px] skew-x-13 origin-top-right',
      },
      shapeRight: {
        last: 'w-1',
        other: 'w-[11px] -ml-[8px] skew-x-13 origin-top-right',
      },
    },
  });

  return (
    <Root
      {...(isInteractive && { type, onClick: props.onClick })}
      role={role}
      aria-selected={ariaSelected}
      className={cn(
        'flex h-8',
        isInteractive &&
          'cursor-pointer appearance-none border-0 bg-transparent p-0',
        isInteractive &&
          'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-neutral-muted-border rounded-sm',
      )}
    >
      {/* 全体的に、斜め部分の表現を自然に保つために px 単位で指定して調整している */}
      {/* 左側の斜めの表現 */}
      <span
        className={cn(
          'border border-r-0 rounded-l-sm',
          styleVariants({
            shapeLeft: props.first ? 'first' : 'other',
            color: props.variant,
          }),
        )}
      />

      <HStack
        className={cn(
          'px-[5px] grow relative border-b border-t items-center gap-2',
          styleVariants({ color: props.variant }),
        )}
      >
        {props.variant === 'completed' && (
          <Icon iconName="Check" colorVariant="success" />
        )}
        {props.variant === 'current' && (
          <Icon
            iconName="Square"
            colorVariant="active"
            size={8}
            className="rotate-45 fill-current"
          />
        )}
        <Text className="text-xs whitespace-nowrap">{props.label}</Text>
      </HStack>

      {/* 右側の斜めの表現 */}
      <span
        className={cn(
          'border border-l-0 rounded-r-sm',
          styleVariants({
            color: props.variant,
            shapeRight: props.last ? 'last' : 'other',
          }),
        )}
      />
    </Root>
  );
};
